// Print QEW Tags, through the one print card. ONE_PRINT_WORDS.md, approved 2026-10-24.
//
// Add Site makes all 99 tags and saves the one PDF to 03 QEW Tags. A QEW tag never changes (the ID is the
// tag), so the card never updates that PDF (list 2, 2026-10-25: "made once"). It is for the lost or damaged
// one: Find it, tap it, Make PDF. Find also looks at who holds each tag, which is what you need when
// you're replacing one. The small print file never goes to Drive.
//
// Same roll as the QR labels (2.25 × 1.0 in on GP 1 x 3), so the same offset (Robert, 9:10 AM).
// Its own module so main.ts carries three hooks, not the card.

import { masterName } from "./drive-folders";
import { tagBucket, tagsIn, type BadgeDoc } from "./people";
import { cardClick, cardFind, downloadFile, newPcState, pickedOf, printCard, shareFile, type PcRoll, type PcState } from "./print-card";
import { readOffset } from "./printer-offset";
import { qewTagsPdf } from "./qew-tag-pdf";
import { QR_PAGE } from "./qr-label-pdf";
import { driverName } from "./driver-page";
import { esc } from "./training-screens";

interface QewPrint {
  site: string;
  pc: PcState;
}

let st: QewPrint | null = null;

export function openQewPrint(site: string): void {
  st = { site, pc: newPcState() };
}

export function qewPrintOpen(): boolean {
  return st !== null;
}

/** Every tag at the site, claimed, unclaimed and out, in tag order. */
function siteTagList(badges: readonly BadgeDoc[], site: string): BadgeDoc[] {
  return [...tagsIn([...badges], site, "claimed"), ...tagsIn([...badges], site, "unclaimed"), ...tagsIn([...badges], site, "out")].sort((a, b) => a["Badge ID"].localeCompare(b["Badge ID"], undefined, { numeric: true }));
}

/** "Claimed · Dana Hill", "Free to claim", "Out of service". */
export function holderLine(b: BadgeDoc): string {
  const k = tagBucket(b);
  if (k === "claimed") return `Claimed · ${b["Holder Name"]}`;
  if (k === "unclaimed") return "Free to claim";
  return b["Holder Name"] ? `Out of service · ${b["Holder Name"]}` : "Out of service";
}

/** The QEW tag drawn small: the QR, the tag ID, the two message lines. */
export function qewArt(tagId: string): string {
  return `<span class="pc-art qr"><span class="pc-qrsq"></span><span class="pc-qrt"><b>${esc(tagId)}</b><i>QUALIFIED ELECTRICAL<br>WORKER BAG TAG</i></span></span>`;
}

/** The one roll: every tag at the site, in tag order. Made once by Add Site, so all are current. */
export function qewRoll(badges: readonly BadgeDoc[], site: string): PcRoll {
  return {
    key: "tags",
    title: "2.25 × 1.0 in",
    sub: `set the driver to ${driverName(QR_PAGE)}`,
    driveName: masterName("tags", site),
    folder: "03 QEW Tags",
    withRooms: false,
    once: true,
    items: siteTagList(badges, site).map((b) => ({ key: b["Badge ID"], room: "", state: "current", why: "", reprintWhy: holderLine(b), art: qewArt(b["Badge ID"]), hay: String(b["Holder Name"] ?? "") })),
  };
}

/** The card's HTML, or "" when it's closed. */
export function qewPrintHtml(badges: readonly BadgeDoc[]): string {
  if (!st) return "";
  return printCard({ title: "Print QEW Tags", unit: "tag", roll: "qrRoll", rolls: [qewRoll(badges, st.site)], state: st.pc, cap: 18 });
}

export interface QewPrintEnv {
  company: string;
  badgeId: string;
  holderName: string;
  repaint: () => void;
  /** The site's badges, read when the tap lands. */
  badges: () => readonly BadgeDoc[];
}

/** A tap inside the card. True when the card took it. */
export function onQewPrintClick(target: HTMLElement, env: QewPrintEnv): boolean {
  if (!st) return false;
  if (target.matches("[data-pc-sheet]")) {
    if (!st.pc.busy) st = null;
    env.repaint();
    return true;
  }
  const el = target.closest<HTMLElement>("[data-pc]");
  if (!el) return false;
  if ((el as HTMLButtonElement).disabled) return true;
  void act(el, env);
  return true;
}

async function act(el: HTMLElement, env: QewPrintEnv): Promise<void> {
  const s = st!;
  const roll = qewRoll(env.badges(), s.site);
  const a = cardClick(el, s.pc, [roll]);
  if (!a) return;
  switch (a.act) {
    case "close":
      if (s.pc.busy) return;
      st = null;
      break;
    case "setup":
      st = null;
      location.hash = "#/settings/printer";
      return;
    case "make":
      await make(roll, env);
      return;
    case "share": {
      const f = s.pc.small.get(roll.key)?.file;
      if (f) await shareFile(f);
      return;
    }
    case "download": {
      const f = s.pc.small.get(roll.key)?.file;
      if (f) downloadFile(f);
      return;
    }
    default:
      break;
  }
  env.repaint();
}

/** The small file: just the picked tags, at this iPad's offset. For the printer. Never to Drive. */
async function make(roll: PcRoll, env: QewPrintEnv): Promise<void> {
  const s = st!;
  const ids = pickedOf(roll, s.pc).map((i) => i.key);
  if (!ids.length || s.pc.busy) return;
  s.pc.busy = `make:${roll.key}`;
  s.pc.error = "";
  env.repaint();
  try {
    const bytes = await qewTagsPdf(ids, readOffset("qrRoll"));
    const name = `${s.site}_QEW_Tags_Print_${ids.length === 1 ? ids[0]!.replace(/^\d{2}-/, "") : `${ids.length}_tags`}.pdf`;
    s.pc.small.set(roll.key, { file: new File([bytes as Uint8Array<ArrayBuffer>], name, { type: "application/pdf" }), count: ids.length });
  } catch (e) {
    s.pc.error = `Could not make the PDF: ${e instanceof Error ? e.message : String(e)}`;
  }
  s.pc.busy = "";
  env.repaint();
}

/** Typing in Find. True when the card took it. */
export function onQewPrintInput(t: HTMLInputElement, repaint: () => void, root: ParentNode): boolean {
  if (!st) return false;
  return cardFind(t, st.pc, repaint, root);
}

/** Leaving the page closes the card. */
export function closeQewPrint(): void {
  if (st && !st.pc.busy) st = null;
}
